import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, Package, BarChart4 } from "lucide-react";
import { ProductEntry, getFunctionalUnitLabel } from "@/types/product/productStep.types";
import { MaterialDocWithId } from "@/types/material";
import { formatNumber, formatCurrency, formatInteger } from "@/utils/numberFormatter";
import { getFactorValue } from "@/components/footprints/utils/calculations/conversions";
import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";

interface ProductTableProps {
  entries: ProductEntry[];
  onEdit: (entry: ProductEntry, idx: number) => void;
  onDelete: (idx: number) => void;
  materialDocs?: MaterialDocWithId[];
}

/**
 * ProductTable
 * Display a list of product entries with edit/delete actions.
 */
export const ProductTable: React.FC<ProductTableProps> = ({ entries, onEdit, onDelete, materialDocs }) => {
  const { t } = useLanguage();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {entries.map((entry, idx) => {
        // Calculate total volume for display
        const unitLabel = (() => {
          switch (entry.functionalUnit.unit) {
            case "m2":
              return t("productTable.m2");
            case "m3":
              return t("productTable.m3");
            case "kg":
              return t("productTable.kg");
            case "t":
              return t("productTable.t");
            default:
              return t("productTable.units");
          }
        })();

        const totalVolume = ["m2", "m3", "kg", "t"].includes(entry.functionalUnit.unit)
          ? entry.annualProduction * entry.functionalUnit.quantity
          : entry.annualProduction;

        const volumeUnit = unitLabel;

        const avgPrice = entry.annualProduction > 0 ? entry.productRevenue / entry.annualProduction : 0;

        // Compute totals from allocated material quantities for the materials table
        const materialsCarbonTotal = entry.materialsUsed.reduce((sum, mat) => {
          const matDoc = materialDocs?.find((m) => m.id === mat.materialId);
          const factor = matDoc ? getFactorValue(matDoc, mat.unit, "carbon") : 0;
          return sum + factor * mat.quantity;
        }, 0);

        const materialsHydricTotal = entry.materialsUsed.reduce((sum, mat) => {
          const matDoc = materialDocs?.find((m) => m.id === mat.materialId);
          const factor = matDoc ? getFactorValue(matDoc, mat.unit, "hydric") : 0;
          return sum + factor * mat.quantity;
        }, 0);

        // Per-functional-unit materials
        const carbonMaterialsPerFU =
          entry.carbonFootprintMaterials ?? (entry.annualProduction > 0 ? materialsCarbonTotal / entry.annualProduction : 0);
        const hydricMaterialsPerFU =
          entry.hydricFootprintMaterials ?? (entry.annualProduction > 0 ? materialsHydricTotal / entry.annualProduction : 0);

        return (
          <Card key={entry.id} className="shadow-sm hover:shadow-md transition-shadow border border-gray-200">
            {/* Header */}
            <CardHeader className="bg-gray-50 border-b border-gray-200 pb-3">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="text-lg font-semibold text-gray-900 mb-2">{entry.name}</CardTitle>
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Package className="h-4 w-4" />
                      <span>{entry.productType}</span>
                    </div>
                    <div className="w-px h-4 bg-gray-300"></div>
                    <span>{entry.stoneTypeName || entry.stoneType}</span>
                  </div>
                </div>
                <div className="flex space-x-1 ml-3">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onEdit(entry, idx)}
                    className="h-8 w-8 p-0 hover:bg-gray-100"
                  >
                    <Pencil className="h-4 w-4 text-gray-500 hover:text-teal-600" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(idx)}
                    className="h-8 w-8 p-0 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 text-gray-500 hover:text-red-600" />
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="py-4">
              {/* Production Metrics */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-1">{t("productTable.functionalUnit")}</div>
                  <div className="font-semibold text-gray-900">
                    {entry.functionalUnit.quantity} {getFunctionalUnitLabel(entry.functionalUnit.unit)}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-1">{t("productTable.annualProduction")}</div>
                  <div className="font-semibold text-gray-900">
                    {formatInteger(entry.annualProduction)} {t("productTable.annualProductionUnit")}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-1">{t("productTable.totalVolume")}</div>
                  <div className="font-semibold text-gray-900">
                    {formatNumber(totalVolume)} {volumeUnit}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-1">{t("productTable.averagePrice")}</div>
                  <div className="font-semibold text-gray-900">
                    {formatNumber(avgPrice)} {t("productTable.eurPerUnit")}
                  </div>
                </div>
              </div>

              {/* Financial & Allocation */}
              <div className="border border-gray-200 rounded-lg p-3 mb-4">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-gray-700">{t("productTable.totalRevenue")}</span>
                  <span className="text-lg font-bold text-gray-900">{formatCurrency(entry.productRevenue)} €</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center">
                    <div className="text-xs text-gray-600">{t("productTable.quantityRatio")}</div>
                    <div className="font-semibold text-gray-900">{(entry.ratioByQuantity * 100).toFixed(2)}%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-600">{t("productTable.revenueRatio")}</div>
                    <div className="font-semibold text-gray-900">{(entry.ratioByRevenue * 100).toFixed(2)}%</div>
                  </div>
                </div>
              </div>

              {/* Materials Used */}
              <div className="mb-6">
                <div className="text-sm font-semibold text-gray-800 mb-3 mt-2">{t("productTable.materialsUsed")}</div>
                {entry.materialsUsed && entry.materialsUsed.length > 0 ? (
                  <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr>
                          <th className="px-3 py-2 text-left uppercase text-gray-500 font-semibold">
                            {t("productTable.material")}
                          </th>
                          <th className="px-3 py-2 text-right uppercase text-gray-500 font-semibold">
                            {t("productTable.quantity")}
                          </th>
                          <th className="px-3 py-2 text-left uppercase text-gray-500 font-semibold">
                            {t("productTable.unit")}
                          </th>
                          <th className="px-3 py-2 text-right uppercase text-gray-500 font-semibold">
                            {t("productTable.carbon")}
                          </th>
                          <th className="px-3 py-2 text-right uppercase text-gray-500 font-semibold">
                            {t("productTable.hydric")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {entry.materialsUsed.map((mat) => {
                          const matDoc = materialDocs?.find((m) => m.id === mat.materialId);
                          const carbon = matDoc ? getFactorValue(matDoc, mat.unit, "carbon") * mat.quantity : 0;
                          const hydric = matDoc ? getFactorValue(matDoc, mat.unit, "hydric") * mat.quantity : 0;
                          return (
                            <tr key={mat.materialId} className="border-t last:border-b-0 hover:bg-gray-50">
                              <td className="px-3 py-2 font-medium text-gray-900">{matDoc?.name || mat.materialId}</td>
                              <td className="px-3 py-2 text-right text-gray-700">{formatNumber(mat.quantity)}</td>
                              <td className="px-3 py-2 text-gray-700">{mat.unit}</td>
                              <td className="px-3 py-2 text-right text-gray-700">{formatNumber(carbon)}</td>
                              <td className="px-3 py-2 text-right text-gray-700">{formatNumber(hydric)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex items-center justify-center text-xs text-gray-400 py-4">
                    <span className="mr-2">{t("productTable.none")}</span> {t("productTable.noMaterials")}
                  </div>
                )}
              </div>

              {/* Footprint Results */}
              <div className="border border-gray-200">
                {/* Carbon Footprint */}
                <div className="bg-teal-50 p-4 border-b border-teal-200">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <BarChart4 className="h-5 w-5 text-teal-700" />
                      <span className="font-medium text-teal-700">{t("productTable.carbonFootprint")}</span>
                    </div>
                    <span className="bg-teal-100 text-teal-700 border-teal-200 text-xs px-2 py-1 rounded">
                      {t("productTable.per")} {entry.functionalUnit.quantity} {" "}
                      {getFunctionalUnitLabel(entry.functionalUnit.unit)}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-teal-600">{t("productTable.materials")}</span>
                      <span className="text-teal-700 font-medium">
                        {formatNumber(carbonMaterialsPerFU)} {t("productTable.kgCO2e")}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-teal-600">{t("productTable.energyAndWaste")}</span>
                      <span className="text-teal-700 font-medium">
                        {formatNumber(entry.carbonFootprintElectricity)} {t("productTable.kgCO2e")}
                      </span>
                    </div>
                    {entry.productFlow !== "extraction" && (
                      <div className="flex justify-between text-sm">
                        <span className="text-teal-600">{t("productTable.transport")}</span>
                        <span className="text-teal-700 font-medium">
                          {formatNumber(entry.carbonFootprintTransport ?? 0)} {t("productTable.kgCO2e")}
                        </span>
                      </div>
                    )}
                    <div className="border-t border-teal-200 pt-2">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-teal-700">{t("productTable.total")}</span>
                        <span className="text-xl font-bold text-teal-800">
                          {formatNumber(
                            entry.productFlow === "extraction"
                              ? (entry.carbonFootprintTotal ?? 0) - (entry.carbonFootprintTransport ?? 0)
                              : entry.carbonFootprintTotal ?? 0 // Transport already included for transformation
                          )} {" "}
                          <span className="text-sm">{t("productTable.kgCO2e")}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Hydric Footprint */}
                <div className="bg-cyan-50 p-4">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <BarChart4 className="h-5 w-5 text-cyan-700" />
                      <span className="font-medium text-cyan-700">{t("productTable.hydricFootprint")}</span>
                    </div>
                    <span className="bg-cyan-100 text-cyan-700 border-cyan-200 text-xs px-2 py-1 rounded">
                      {t("productTable.per")} {entry.functionalUnit.quantity} {" "}
                      {getFunctionalUnitLabel(entry.functionalUnit.unit)}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-cyan-600">{t("productTable.materials")}</span>
                      <span className="text-cyan-700 font-medium">
                        {formatNumber(hydricMaterialsPerFU)} {t("productTable.m3")}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-cyan-600">{t("productTable.energy")}</span>
                      <span className="text-cyan-700 font-medium">
                        {formatNumber(entry.hydricFootprintElectricity)} {t("productTable.m3")}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-cyan-600">{t("productTable.water")}</span>
                      <span className="text-cyan-700 font-medium">
                        {formatNumber(entry.hydricFootprintWater)} {t("productTable.m3")}
                      </span>
                    </div>
                    {entry.productFlow !== "extraction" && (
                      <div className="flex justify-between text-sm">
                        <span className="text-cyan-600">{t("productTable.transport")}</span>
                        <span className="text-cyan-700 font-medium">
                          {formatNumber(entry.hydricFootprintTransport ?? 0)} {t("productTable.m3")}
                        </span>
                      </div>
                    )}
                    <div className="border-t border-cyan-200 pt-2">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-cyan-700">{t("productTable.total")}</span>
                        <span className="text-xl font-bold text-cyan-800">
                          {formatNumber(
                            entry.productFlow === "extraction"
                              ? (entry.hydricFootprintTotal ?? 0) - (entry.hydricFootprintTransport ?? 0)
                              : entry.hydricFootprintTotal ?? 0 // Transport already included for transformation
                          )} {" "}
                          <span className="text-sm">{t("productTable.m3")}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
